<template>
  <div class="gantt-wrapper">
    <div class="gantt-toolbar">
      <div class="d-flex align-items-center flex-wrap">
        <div class="mr-4 d-flex align-items-center">
          <span class="text-muted small font-weight-bold mr-2"
            >Hiển thị theo:</span
          >
          <div class="btn-group btn-group-sm shadow-sm">
            <button
              class="btn btn-white"
              :class="{ active: viewMode === 'Day' }"
              @click="changeView('Day')"
            >
              Ngày
            </button>
            <button
              class="btn btn-white"
              :class="{ active: viewMode === 'Week' }"
              @click="changeView('Week')"
            >
              Tuần
            </button>
            <button
              class="btn btn-white"
              :class="{ active: viewMode === 'Month' }"
              @click="changeView('Month')"
            >
              Tháng
            </button>
          </div>
        </div>

        <div class="mr-2" v-if="viewMode === 'Day'">
          <select
            class="form-control form-control-sm custom-select"
            v-model="selectedMonth"
          >
            <option v-for="m in 12" :key="m" :value="m - 1">
              Tháng {{ m }}
            </option>
          </select>
        </div>

        <div>
          <select
            class="form-control form-control-sm custom-select"
            v-model="selectedYear"
          >
            <option v-for="y in yearOptions" :key="y" :value="y">
              Năm {{ y }}
            </option>
          </select>
        </div>

        <button
          class="btn btn-sm btn-light ml-auto text-primary"
          @click="resetToToday"
        >
          <i class="la la-calendar-check-o"></i> Hôm nay
        </button>
      </div>
    </div>

    <div class="gantt-container" ref="scrollContainer">
      <div class="gantt-header">
        <div class="gantt-corner-cell">Dự án / Công việc</div>

        <div
          class="gantt-timeline-header"
          :style="{ width: totalWidth + 'px' }"
        >
          <div
            class="date-cell"
            v-for="(col, index) in timelineColumns"
            :key="index"
            :style="{ width: columnWidth + 'px' }"
            :class="{ 'today-col': col.isToday }"
          >
            <div class="day-name">{{ col.labelTop }}</div>
            <div class="day-num">{{ col.labelBottom }}</div>
          </div>
        </div>
      </div>

      <div class="gantt-body">
        <div
          v-for="task in computedTasks"
          :key="task.id"
          class="gantt-row"
          @click="emit('select-task', task)"
        >
          <div class="gantt-sticky-col">
            <div class="task-card">
              <div class="task-icon" :style="{ background: task.color }">
                <img
                  v-if="task.avatar"
                  :src="task.avatar"
                  class="task-avatar-img"
                />
                <span v-else>{{ task.shortName || task.name.charAt(0) }}</span>
              </div>
              <div class="task-info">
                <div class="task-title" :title="task.name">{{ task.name }}</div>
                <div class="task-date small text-muted">
                  {{ formatDateVN(task.realStartDate) }} -
                  {{ formatDateVN(task.realEndDate) }}
                </div>
              </div>
            </div>
          </div>

          <div class="gantt-timeline-row" :style="{ width: totalWidth + 'px' }">
            <div class="grid-background">
              <div
                class="grid-line"
                v-for="(col, i) in timelineColumns"
                :key="i"
                :style="{ width: columnWidth + 'px' }"
                :class="{ 'today-line': col.isToday }"
              ></div>
            </div>

            <div v-if="task.isVisible" class="bar-wrapper" :style="task.style">
              <div
                class="bar-bg"
                :style="{ background: hexToRgba(task.color, 0.2) }"
              ></div>
              <div
                class="bar-progress"
                :style="{ width: task.progress + '%', background: task.color }"
              ></div>
              <div class="bar-content">
                <div class="bar-icon-circle">
                  <img
                    v-if="task.avatar"
                    :src="task.avatar"
                    class="bar-avatar-img"
                  />
                  <span v-else class="dots"></span>
                </div>
                <span
                  class="bar-label"
                  v-if="parseFloat(task.style.width) > 60"
                  >{{ task.name }}</span
                >
                <span
                  class="bar-percent"
                  :style="{ color: task.progress > 50 ? '#fff' : '#333' }"
                  >{{ task.progress }}%</span
                >
                <i
                  v-if="isTaskOverflow(task)"
                  class="la la-angle-double-right ml-auto mr-2"
                  style="color: rgba(255, 255, 255, 0.8); font-size: 12px"
                  title="Kéo sang phải để xem tiếp"
                >
                </i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";

const props = defineProps({
  tasks: { type: Array, default: () => [] },
  projectStartDate: {
    type: String,
    default: new Date().toLocaleDateString("sv-SE"),
  },
});

const emit = defineEmits(["select-task"]);
const scrollContainer = ref(null);

// --- STATE ---
const today = new Date();
const viewMode = ref("Day");
const selectedMonth = ref(today.getMonth());
const selectedYear = ref(today.getFullYear());

// Tự động cập nhật lịch khi props thay đổi
watch(
  () => props.projectStartDate,
  (newVal) => {
    if (newVal) {
      const d = new Date(newVal);
      if (!isNaN(d.getTime())) {
        selectedMonth.value = d.getMonth();
        selectedYear.value = d.getFullYear();
      }
    }
  },
  { immediate: true }
);

const yearOptions = computed(() => {
  const current = new Date().getFullYear();
  const arr = [];
  for (let i = current - 5; i <= current + 5; i++) arr.push(i);
  return arr;
});

const columnWidth = computed(() => {
  switch (viewMode.value) {
    case "Day":
      return 50;
    case "Week":
      return 120;
    case "Month":
      return 150;
    default:
      return 60;
  }
});

// --- LOGIC TIMELINE (ĐÃ FIX LỖI BIẾN YEAR) ---
const timelineColumns = computed(() => {
  const cols = [];
  const currentYear = selectedYear.value; // Đổi tên biến để tránh conflict

  if (viewMode.value === "Day") {
    const daysInMonth = new Date(
      currentYear,
      selectedMonth.value + 1,
      0
    ).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(currentYear, selectedMonth.value, d);
      const isToday = dateObj.toDateString() === today.toDateString();
      const daysVN = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
      cols.push({
        date: dateObj,
        labelTop: daysVN[dateObj.getDay()],
        labelBottom: d,
        isToday: isToday,
      });
    }
  } else if (viewMode.value === "Week") {
    let d = new Date(currentYear, 0, 1);
    // Lùi về thứ 2
    const dayOfWeek = d.getDay() || 7;
    if (dayOfWeek !== 1) d.setDate(d.getDate() - dayOfWeek + 1);

    while (d.getFullYear() <= currentYear + 1) {
      const weekStart = new Date(d);
      const weekEnd = new Date(d);
      weekEnd.setDate(d.getDate() + 6);

      if (weekStart.getFullYear() > currentYear) break;

      const isCurrentWeek = today >= weekStart && today <= weekEnd;
      cols.push({
        date: weekStart,
        labelTop: `T${weekEnd.getMonth() + 1}`,
        labelBottom: `W${getWeekNumber(weekStart)}`,
        isToday: isCurrentWeek,
      });
      d.setDate(d.getDate() + 7);
    }
  } else if (viewMode.value === "Month") {
    for (let m = 0; m < 12; m++) {
      const dateObj = new Date(currentYear, m, 1);
      const isCurrentMonth =
        today.getMonth() === m && today.getFullYear() === currentYear;
      cols.push({
        date: dateObj,
        labelTop: currentYear,
        labelBottom: `Thg ${m + 1}`,
        isToday: isCurrentMonth,
      });
    }
  }
  return cols;
});

const totalWidth = computed(
  () => timelineColumns.value.length * columnWidth.value
);

// --- LOGIC TASK (ĐÃ FIX NHẬN DATE STRING) ---
const computedTasks = computed(() => {
  if (timelineColumns.value.length === 0) return [];

  const timelineStartMs = timelineColumns.value[0].date.getTime();
  const MS_PER_DAY = 86400000;
  let msPerCol = MS_PER_DAY;

  if (viewMode.value === "Week") msPerCol = MS_PER_DAY * 7;
  if (viewMode.value === "Month") msPerCol = MS_PER_DAY * 30.44;

  const pxPerMs = columnWidth.value / msPerCol;

  return props.tasks.map((task) => {
    // Hỗ trợ cả startDate (String) và startDay (Number - Legacy)
    let sDate = task.startDate || task.startDay;
    let eDate = task.endDate || task.duration;

    // Nếu không có ngày thì ẩn
    if (!sDate || !eDate) return { ...task, isVisible: false };

    const startMs = new Date(sDate).getTime();
    const endMs = new Date(eDate).getTime();

    if (isNaN(startMs) || isNaN(endMs)) return { ...task, isVisible: false };

    const diffTime = startMs - timelineStartMs;
    const durationTime = endMs - startMs;

    const left = diffTime * pxPerMs;
    const width = durationTime * pxPerMs;
    const isVisible = left + width > 0;

    return {
      ...task,
      realStartDate: sDate,
      realEndDate: eDate,
      isVisible,
      style: { left: `${left}px`, width: `${Math.max(width, 10)}px` },
    };
  });
});

// --- ACTIONS ---
const changeView = (mode) => {
  viewMode.value = mode;
  if (mode === "Day") resetToToday();
};
const resetToToday = () => {
  const d = props.projectStartDate
    ? new Date(props.projectStartDate)
    : new Date();
  if (!isNaN(d.getTime())) {
    selectedMonth.value = d.getMonth();
    selectedYear.value = d.getFullYear();
  }
  nextTick(() => {
    const el = scrollContainer.value.querySelector(".today-col");
    if (el)
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
  });
};
function getWeekNumber(d) {
  d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}
const formatDateVN = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt.getTime())
    ? ""
    : `${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
};
const hexToRgba = (hex, alpha) => {
  if (!hex) return `rgba(200,200,200,${alpha})`;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    let c = hex.substring(1).split("");
    if (c.length == 3) c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    c = "0x" + c.join("");
    return (
      "rgba(" +
      [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",") +
      "," +
      alpha +
      ")"
    );
  }
  return hex;
};
const isTaskOverflow = (task) => {
  if (timelineColumns.value.length === 0) return false;
  const lastCol = timelineColumns.value[timelineColumns.value.length - 1];
  const limitDate = new Date(lastCol.date);
  if (viewMode.value === "Month") {
    limitDate.setMonth(limitDate.getMonth() + 1);
  } else if (viewMode.value === "Week") {
    limitDate.setDate(limitDate.getDate() + 7);
  } else {
    limitDate.setDate(limitDate.getDate() + 1);
  }
  // Cần convert sang Date để so sánh
  const tEnd = new Date(task.realEndDate);
  return tEnd > limitDate;
};
</script>

<style scoped>
/* --- KHUNG BAO --- */
.gantt-wrapper {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: auto;
  max-height: 700px;
  min-height: 300px;
}
.gantt-toolbar {
  padding: 10px 15px;
  background: #fff;
  border-bottom: 1px solid #eee;
  z-index: 50;
}
.custom-select {
  width: auto;
  display: inline-block;
  margin-right: 5px;
  font-weight: 600;
  color: #555;
  border: 1px solid #eee;
}

/* --- CONTAINER CUỘN --- */
.gantt-container {
  overflow: auto;
  flex-grow: 1;
  position: relative;
  scrollbar-width: thin;
}

/* --- HEADER --- */
/* FIX PC: Thêm min-width: fit-content để KHÔNG bị trôi khi scroll ngang */
.gantt-header {
  display: flex;
  position: sticky;
  top: 0;
  z-index: 40;
  background: #f9f9f9;
  border-bottom: 1px solid #eee;
  min-width: fit-content; /* <--- QUAN TRỌNG CHO PC */
}

/* CỘT GÓC TRÁI (STICKY) */
.gantt-corner-cell {
  min-width: 240px;
  max-width: 240px;
  position: sticky;
  left: 0;
  z-index: 50;
  background: #f9f9f9;
  border-right: 1px solid #ddd;
  padding: 10px;
  font-weight: bold;
  color: #666;
  display: flex;
  align-items: center;
}

.gantt-timeline-header {
  display: flex;
}
.date-cell {
  border-right: 1px dashed #eee;
  text-align: center;
  padding: 5px 0;
  flex-shrink: 0;
  background: #f9f9f9;
}
.date-cell.today-col {
  background: #e6f7ff;
  border-bottom: 2px solid #1890ff;
}
.day-name {
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
}
.day-num {
  font-weight: bold;
  font-size: 13px;
  color: #333;
}

/* --- BODY --- */
/* FIX PC: Thêm min-width để đồng bộ với Header */
.gantt-row {
  display: flex;
  border-bottom: 1px solid #f5f5f5;
  min-width: fit-content; /* <--- QUAN TRỌNG CHO PC */
  cursor: pointer;
}
.gantt-row:hover {
  background: #fafafa;
}

/* CỘT TRÁI BODY (STICKY) */
.gantt-sticky-col {
  min-width: 240px;
  max-width: 240px;
  position: sticky;
  left: 0;
  z-index: 30;
  background: #fff;
  border-right: 1px solid #ddd;
  padding: 8px;
  box-shadow: 4px 0 10px rgba(0, 0, 0, 0.03);
}

/* --- MOBILE FIX: TẮT STICKY HOÀN TOÀN --- */
@media (max-width: 768px) {
  .gantt-corner-cell,
  .gantt-sticky-col {
    position: static !important; /* Tắt dính để kéo lướt */
    box-shadow: none !important;
    min-width: 150px !important; /* Thu nhỏ chút cho gọn */
    max-width: 150px !important;
  }

  /* Reset Header & Row để bảng bung lụa */
  .gantt-header,
  .gantt-row {
    min-width: auto;
    width: max-content;
  }
}

/* --- CÁC PHẦN KHÁC GIỮ NGUYÊN --- */
.task-card {
  display: flex;
  align-items: center;
}
.task-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  margin-right: 10px;
  overflow: hidden;
  flex-shrink: 0;
}
.task-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.task-info {
  overflow: hidden;
}
.task-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.task-date {
  font-size: 11px;
}

.gantt-timeline-row {
  position: relative;
  height: 60px;
  overflow: hidden;
}
.grid-background {
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 0;
}
.grid-line {
  border-right: 1px dashed #f9f9f9;
  height: 100%;
  flex-shrink: 0;
}
.grid-line.today-line {
  background: rgba(24, 144, 255, 0.05);
  border-right: 1px solid rgba(24, 144, 255, 0.2);
}

.bar-wrapper {
  position: absolute;
  top: 12px;
  height: 36px;
  border-radius: 18px;
  overflow: hidden;
  transition: all 0.3s ease;
}
.bar-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}
.bar-progress {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  z-index: 2;
  border-radius: 18px 0 0 18px;
}
.bar-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  display: flex;
  align-items: center;
  padding-left: 5px;
}
.bar-icon-circle {
  width: 26px;
  height: 26px;
  background: #fff;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}
.bar-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 2px solid #fff;
}
.dots {
  width: 8px;
  height: 8px;
  background: #333;
  border-radius: 50%;
  opacity: 0.3;
}
.bar-label {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: auto;
  text-shadow: 0 0 2px rgba(255, 255, 255, 0.8);
}
.bar-percent {
  font-size: 11px;
  font-weight: 800;
  padding-right: 8px;
  margin-left: 5px;
}
</style>
